import { useState, useEffect, useCallback, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import {
  Wallet, PaperPlaneTilt, Plus, ClockCounterClockwise, User,
  ArrowsClockwise, SignOut, SignIn, UserPlus, ShieldWarning,
  Copy, Buildings, GlobeHemisphereWest, CheckCircle, WarningCircle,
  ArrowUpRight, ArrowDownRight, CaretRight, TrendUp, CalendarCheck
} from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "https://bank.trilliumbucks.com");
const API = `${BACKEND_URL}/api`;
const WEBHOOK_URL = process.env.REACT_APP_WEBHOOK_URL || `${BACKEND_URL}/api/webhook/bd-signup`;

const emailToUsername = (email) => {
  if (!email || !email.includes("@")) return "";
  return email.split("@")[0].toLowerCase().replace(/[^a-z0-9_.]/g, "_");
};

// ─── Login Screen ─────────────────────────────────────────────
const LoginScreen = ({ onLogin, isLoading, onCreateAccount }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showRegister, setShowRegister] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [registerFirstName, setRegisterFirstName] = useState("");
  const [registerLastName, setRegisterLastName] = useState("");
  const [registerPassword, setRegisterPassword] = useState("");
  const [isRegistering, setIsRegistering] = useState(false);

  useEffect(() => {
    // Pre-populate email from BD webhook redirect URL params (e.g. ?email=user@example.com)
    const params = new URLSearchParams(window.location.search);
    const urlEmail = params.get("email") || params.get("member_email");
    if (urlEmail) {
      setEmail(urlEmail.trim());
    } else {
      const saved = localStorage.getItem("tb_user_email");
      if (saved) setEmail(saved);
    }
    if (localStorage.getItem("tb_session_expired") === "true") {
      setSessionExpired(true);
      localStorage.removeItem("tb_session_expired");
    }
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Enter a valid email address");
      return;
    }
    if (!password) {
      toast.error("Enter your password");
      return;
    }
    onLogin(email, password);
  };

  const handleRegister = () => {
    if (!email || !email.includes("@")) {
      toast.error("Enter your email first");
      return;
    }
    setShowRegister(true);
  };

  const handleCreateAccount = async () => {
    if (!email || !email.includes("@")) {
      toast.error("Enter your email first");
      return;
    }
    if (!registerPassword) {
      toast.error("Enter a password to create your account.");
      return;
    }

    setIsRegistering(true);
    const success = await onCreateAccount({
      email,
      password: registerPassword,
      first_name: registerFirstName,
      last_name: registerLastName,
    });
    setIsRegistering(false);

    if (success) {
      setShowRegister(false);
      setRegisterPassword("");
      setRegisterFirstName("");
      setRegisterLastName("");
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left panel — brand */}
      <div
        className="hidden lg:flex lg:w-[45%] relative items-end p-12"
        style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1690049089775-b915262c89d0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGdvbGQlMjB3aGl0ZSUyMGx1eHVyeXxlbnwwfHx8fDE3NzU5MzU4MjB8MA&ixlib=rb-4.1.0&q=85')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B192C]/90 via-[#0B192C]/40 to-transparent" />
        <div className="relative z-10 max-w-md">
          <h1 className="font-display text-5xl font-semibold text-white tracking-tight leading-[1.1] mb-4">
            Trillium Bucks
          </h1>
          <p className="text-white/60 text-sm font-body tracking-wide">
            A Division of The Alliance Group
          </p>
          <div className="mt-8 h-px w-16 bg-[#D4AF37]" />
          <p className="mt-6 text-white/80 text-base font-body leading-relaxed">
            The corporate hub for TB Bucks, advertising credits, and member wallet control.
          </p>
          <div className="mt-8 space-y-4 text-sm text-white/80 font-body">
            <p>Designed for businesses, member organizations, and directory publishers.</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Request ad credits with clear campaign details.</li>
              <li>Track wallet movements and member balances.</li>
              <li>Use webhook-ready Brilliant Directories onboarding.</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex items-center justify-center bg-white px-6 py-12">
        <div className="w-full max-w-md">
          {/* Mobile branding */}
          <div className="lg:hidden mb-10">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-[#0B192C] flex items-center justify-center">
                <span className="text-[#D4AF37] font-display font-bold text-xl">T</span>
              </div>
              <div>
                <h1 className="font-display text-2xl font-semibold text-[#0B192C]">Trillium Bucks</h1>
                <p className="text-xs text-[#64748B] font-body">A Division of The Alliance Group</p>
              </div>
            </div>
          </div>

          {sessionExpired && (
            <div data-testid="session-expired-banner" className="mb-6 p-4 bg-red-50 border-l-4 border-red-700 flex items-center gap-3 animate-fade-in">
              <ShieldWarning size={20} weight="duotone" className="text-red-700 shrink-0" />
              <div>
                <p className="font-body font-semibold text-red-800 text-sm">Session Expired</p>
                <p className="text-xs text-red-600">Please log in again to continue.</p>
              </div>
            </div>
          )}

          <div className="hidden lg:block mb-10">
            <h2 className="font-display text-3xl font-semibold text-[#0B192C] tracking-tight">Welcome back</h2>
            <p className="text-[#64748B] text-sm mt-2 font-body">Sign in with your email and password</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="text-xs font-semibold text-[#64748B] uppercase tracking-[0.2em] mb-2 block font-body">
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                data-testid="email-input"
                className="w-full px-4 py-3 border border-[#E2E8F0] bg-white text-sm font-body text-[#0B192C] placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#0B192C] focus:border-transparent transition-all"
              />
              {email && email.includes("@") && (
                <p className="text-xs text-[#64748B] mt-1.5 font-body">
                  User ID: <span className="font-mono text-[#0B192C] font-medium">{emailToUsername(email)}</span>
                </p>
              )}
            </div>

            <div>
              <label className="text-xs font-semibold text-[#64748B] uppercase tracking-[0.2em] mb-2 block font-body">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Your account password"
                data-testid="password-input"
                onKeyDown={(e) => e.key === "Enter" && handleSubmit(e)}
                className="w-full px-4 py-3 border border-[#E2E8F0] bg-white text-sm font-body text-[#0B192C] placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#0B192C] focus:border-transparent transition-all"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading || !email || !password}
              data-testid="connect-btn"
              className="w-full py-3 bg-[#0B192C] text-white hover:bg-[#1A2E4A] font-body font-medium text-sm tracking-wide transition-all duration-200 disabled:opacity-40 flex items-center justify-center gap-2"
            >
              <SignIn size={18} weight="bold" />
              {isLoading ? "Signing in..." : "Sign In"}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={handleRegister}
              disabled={isLoading}
              data-testid="create-account-btn"
              className="text-sm text-[#64748B] hover:text-[#0B192C] font-body transition-colors inline-flex items-center gap-1"
            >
              Don't have an account? <span className="text-[#D4AF37] font-medium">Register</span>
            </button>
          </div>

          <div className="mt-3 text-center">
            <a href="/vault" className="text-sm text-[#0B192C] hover:text-[#D4AF37] font-medium">
              Visit the Vault landing page
            </a>
          </div>

          {/* Register panel */}
          {showRegister && (
            <div className="mt-6 p-5 bg-[#F8F9FA] border border-[#E2E8F0] animate-fade-in">
              <div className="space-y-5">
                <div className="flex items-start gap-3">
                  <UserPlus size={20} weight="duotone" className="text-[#D4AF37] mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <p className="font-body font-semibold text-[#0B192C] text-sm">Create Your Account</p>
                    <p className="text-xs text-[#64748B] mt-1 font-body">
                      Your user ID defaults to your email with the @ removed. Enter a password and your name to create a secure account.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold text-[#64748B] uppercase tracking-[0.2em] mb-2 block font-body">First Name</label>
                    <input
                      type="text"
                      value={registerFirstName}
                      onChange={(e) => setRegisterFirstName(e.target.value)}
                      placeholder="First Name"
                      className="w-full px-4 py-3 border border-[#E2E8F0] bg-white text-sm font-body text-[#0B192C] placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#0B192C] focus:border-transparent transition-all"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-[#64748B] uppercase tracking-[0.2em] mb-2 block font-body">Last Name</label>
                    <input
                      type="text"
                      value={registerLastName}
                      onChange={(e) => setRegisterLastName(e.target.value)}
                      placeholder="Last Name"
                      className="w-full px-4 py-3 border border-[#E2E8F0] bg-white text-sm font-body text-[#0B192C] placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#0B192C] focus:border-transparent transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-semibold text-[#64748B] uppercase tracking-[0.2em] mb-2 block font-body">Password</label>
                  <input
                    type="password"
                    value={registerPassword}
                    onChange={(e) => setRegisterPassword(e.target.value)}
                    placeholder="Enter a password"
                    className="w-full px-4 py-3 border border-[#E2E8F0] bg-white text-sm font-body text-[#0B192C] placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#0B192C] focus:border-transparent transition-all"
                  />
                </div>

                <div className="p-3 bg-white border border-[#E2E8F0] flex items-center justify-between">
                  <div>
                    <p className="text-xs text-[#64748B]">Automatic User ID</p>
                    <code className="text-base font-mono font-semibold text-[#0B192C]">{emailToUsername(email)}</code>
                  </div>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(emailToUsername(email));
                      toast.success("Username copied!");
                    }}
                    data-testid="copy-username-btn"
                    className="p-1.5 hover:bg-[#F8F9FA] transition-colors"
                  >
                    <Copy size={16} weight="duotone" className="text-[#64748B]" />
                  </button>
                </div>

                <button
                  onClick={handleCreateAccount}
                  disabled={isRegistering}
                  data-testid="register-create-account-btn"
                  className="w-full py-3 bg-[#D4AF37] text-[#0B192C] hover:bg-[#B5952F] font-body font-medium text-sm transition-colors flex items-center justify-center gap-2"
                >
                  <CaretRight size={16} weight="bold" />
                  {isRegistering ? "Creating account..." : "Create Account"}
                </button>

                <p className="text-xs text-[#64748B]">After creating your account you will be logged in and taken to the dashboard automatically.</p>
              </div>
            </div>
          )}

          <p className="mt-10 text-center text-xs text-[#94A3B8] font-body">
            &copy; {new Date().getFullYear()} Trillium Bucks &middot; The Alliance Group
          </p>
        </div>
      </div>
    </div>
  );
};

// ─── Dashboard ────────────────────────────────────────────────
const DashboardView = ({
  userEmail, username, sessionToken, balance, availableBalance, currencySymbol,
  transactions, isLoading, onLogout, onFetchBalance, onFetchTransactions, onTransfer, onPurchase,
  onSessionExpired,
}) => {
  const [transferRecipient, setTransferRecipient] = useState("");
  const [transferAmount, setTransferAmount] = useState("");
  const [transferDescription, setTransferDescription] = useState("");
  const [purchaseAmount, setPurchaseAmount] = useState("");
  const [purchaseNotes, setPurchaseNotes] = useState("");
  const [profileName, setProfileName] = useState("");
  const [profilePhone, setProfilePhone] = useState("");
  const [profileBusiness, setProfileBusiness] = useState("");

  useEffect(() => {
    const saved = localStorage.getItem("tb_profile");
    if (saved) {
      try {
        const p = JSON.parse(saved);
        setProfileName(p.name || "");
        setProfilePhone(p.phone || "");
        setProfileBusiness(p.business || "");
      } catch {}
    }
  }, []);

  const handleTransfer = async (e) => {
    e.preventDefault();
    if (!transferRecipient || !transferAmount) {
      toast.error("Enter recipient and amount");
      return;
    }
    const ok = await onTransfer(transferRecipient, transferAmount, transferDescription);
    if (ok) {
      setTransferRecipient("");
      setTransferAmount("");
      setTransferDescription("");
    }
  };

  const handlePurchase = async (e) => {
    e.preventDefault();
    if (!purchaseAmount) {
      toast.error("Enter an amount");
      return;
    }
    const ok = await onPurchase(purchaseAmount, purchaseNotes);
    if (ok) {
      setPurchaseAmount("");
      setPurchaseNotes("");
    }
  };

  const saveProfile = () => {
    localStorage.setItem("tb_profile", JSON.stringify({ name: profileName, phone: profilePhone, business: profileBusiness }));
    toast.success("Profile saved");
  };

  const formatDate = (d) => {
    if (!d) return "";
    return new Date(d).toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" });
  };

  const sentTransactions = transactions.filter((t) => t.amount && parseFloat(t.amount) < 0);
  const receivedTransactions = transactions.filter((t) => t.amount && parseFloat(t.amount) > 0);
  const sentVolume = sentTransactions.reduce((sum, t) => sum + Math.abs(parseFloat(t.amount || 0)), 0);
  const receivedVolume = receivedTransactions.reduce((sum, t) => sum + parseFloat(t.amount || 0), 0);
  const netFlow = receivedVolume - sentVolume;
  const topCounterparty = transactions.reduce((acc, tx) => {
    const label = (tx.relatedUser || "").trim();
    if (!label) return acc;
    acc[label] = (acc[label] || 0) + 1;
    return acc;
  }, {});
  const highestCounterparty = Object.entries(topCounterparty).sort((a, b) => b[1] - a[1])[0]?.[0] || "Not enough data";

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Header */}
      <header className="bg-[#0B192C] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-[#D4AF37] flex items-center justify-center">
                <span className="text-[#0B192C] font-display font-bold text-base">T</span>
              </div>
              <span className="font-display text-lg font-semibold text-white tracking-tight">Trillium Bucks</span>
            </div>
            <div className="flex items-center gap-5">
              <div className="hidden sm:flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full" />
                <span className="text-xs text-white/60 font-body">{userEmail}</span>
              </div>
              <button
                onClick={onLogout}
                data-testid="logout-btn"
                className="flex items-center gap-1.5 text-xs text-white/50 hover:text-white transition-colors font-body"
              >
                <SignOut size={14} weight="bold" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="dashboard-grid-glow max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid gap-5 mb-6 lg:grid-cols-[1.6fr_1fr]">
          <div className="relative overflow-hidden bg-[#0B192C] rounded-[2rem] p-8 shadow-lg ring-1 ring-white/10 text-white">
            <div className="pointer-events-none absolute -top-20 -right-20 h-56 w-56 rounded-full bg-[#D4AF37]/20 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-24 -left-12 h-52 w-52 rounded-full bg-white/10 blur-3xl" />
            <p className="text-xs uppercase tracking-[0.3em] text-[#94A3B8] mb-4">Corporate control center</p>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold leading-tight">Your all-in-one Trillium Bucks business dashboard</h2>
            <p className="mt-5 text-sm leading-7 text-[#CBD5E1] max-w-2xl">
              Manage member credit workflows, track wallet movement, and request advertising credits with campaign context. Designed for business operators, publishers, and community leaders.
            </p>
            <div className="mt-8 grid gap-3 sm:grid-cols-2">
              <div className="rounded-3xl bg-white/10 p-4">
                <p className="text-xs uppercase tracking-[0.2em] text-[#94A3B8]">Operations</p>
                <p className="mt-2 text-sm font-semibold text-white">Fast credit request review</p>
              </div>
              <div className="rounded-3xl bg-white/10 p-4">
                <p className="text-xs uppercase tracking-[0.2em] text-[#94A3B8]">Control</p>
                <p className="mt-2 text-sm font-semibold text-white">Member balance visibility</p>
              </div>
            </div>
          </div>
          <div className="grid gap-5">
            <div className="rounded-[2rem] border border-[#E2E8F0] bg-white p-6 shadow-sm">
              <p className="text-xs uppercase tracking-[0.2em] text-[#64748B]">Business value</p>
              <h3 className="mt-3 font-display text-xl text-[#0B192C] font-semibold">Earn more with member credits</h3>
              <p className="mt-3 text-sm text-[#64748B] leading-6">Empower members with TB Bucks for advertising, referrals, and community purchases while keeping operations centralized.</p>
            </div>
            <div className="rounded-[2rem] border border-[#E2E8F0] bg-white p-6 shadow-sm">
              <p className="text-xs uppercase tracking-[0.2em] text-[#64748B]">Integration</p>
              <h3 className="mt-3 font-display text-xl text-[#0B192C] font-semibold">Brilliant Directories-ready</h3>
              <p className="mt-3 text-sm text-[#64748B] leading-6">Use the Vault as a member-ready portal with practical workflows for onboarding, credits, and growth planning.</p>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-5">

          {/* ── Balance Hero ── */}
          <div data-testid="balance-widget" className="md:col-span-2 bg-white border border-[#E2E8F0] shadow-sm animate-fade-in">
            <div className="p-8 lg:p-10">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-2">
                  <Wallet size={18} weight="duotone" className="text-[#D4AF37]" />
                  <span className="text-xs font-semibold text-[#64748B] uppercase tracking-[0.2em] font-body">Account Balance</span>
                </div>
                <button
                  onClick={onFetchBalance}
                  disabled={isLoading}
                  data-testid="refresh-balance-btn"
                  className="p-2 hover:bg-[#F8F9FA] transition-colors disabled:opacity-40"
                >
                  <ArrowsClockwise size={16} weight="bold" className={`text-[#64748B] ${isLoading ? "animate-spin" : ""}`} />
                </button>
              </div>
              <div className="mb-3">
                <span className="font-display text-5xl sm:text-6xl lg:text-7xl font-semibold text-[#0B192C] tracking-tight" data-testid="balance-display">
                  {balance}
                </span>
                <span className="text-2xl lg:text-3xl text-[#D4AF37] ml-3 font-display">{currencySymbol}</span>
              </div>
              <div className="flex items-center gap-6 text-sm font-body">
                <div>
                  <span className="text-[#64748B]">Available </span>
                  <span className="text-[#0B192C] font-medium">{availableBalance} {currencySymbol}</span>
                </div>
                {username && (
                  <div>
                    <span className="text-[#64748B]">Account </span>
                    <span className="text-[#D4AF37] font-mono text-xs font-medium">{username}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── Quick Stats ── */}
          <div className="bg-white border border-[#E2E8F0] shadow-sm p-6 animate-fade-in" style={{ animationDelay: "0.05s" }}>
            <div className="flex items-center gap-2 mb-5">
              <ArrowUpRight size={16} weight="bold" className="text-emerald-600" />
              <span className="text-xs font-semibold text-[#64748B] uppercase tracking-[0.2em] font-body">Sent</span>
            </div>
            <p className="font-display text-4xl font-semibold text-[#0B192C]">
              {sentTransactions.length}
            </p>
            <p className="text-sm text-[#64748B] mt-1 font-body">Transactions</p>
          </div>

          <div className="bg-white border border-[#E2E8F0] shadow-sm p-6 animate-fade-in" style={{ animationDelay: "0.1s" }}>
            <div className="flex items-center gap-2 mb-5">
              <ArrowDownRight size={16} weight="bold" className="text-[#D4AF37]" />
              <span className="text-xs font-semibold text-[#64748B] uppercase tracking-[0.2em] font-body">Received</span>
            </div>
            <p className="font-display text-4xl font-semibold text-[#0B192C]">
              {receivedTransactions.length}
            </p>
            <p className="text-sm text-[#64748B] mt-1 font-body">Transactions</p>
          </div>

          <div className="md:col-span-3 lg:col-span-4 bg-white border border-[#E2E8F0] shadow-sm p-6 animate-fade-in" style={{ animationDelay: "0.12s" }}>
            <div className="mb-4">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#64748B] font-body">Executive Snapshot</p>
              <h3 className="font-display text-xl font-semibold text-[#0B192C]">Business metrics that help owners decide faster</h3>
            </div>
            <div className="grid gap-4 md:grid-cols-4">
              <div className="rounded-3xl border border-[#E2E8F0] bg-[#F8F9FA] p-5">
                <div className="flex items-center gap-2 text-[#64748B] text-xs uppercase tracking-[0.2em]">
                  <TrendUp size={14} weight="bold" />
                  Net Flow
                </div>
                <p className="mt-3 text-2xl font-display text-[#0B192C]">{netFlow >= 0 ? "+" : ""}{netFlow.toFixed(2)} TB</p>
                <p className="text-sm text-[#64748B] mt-2">Based on current visible history.</p>
              </div>
              <div className="rounded-3xl border border-[#E2E8F0] bg-[#F8F9FA] p-5">
                <p className="text-xs uppercase tracking-[0.2em] text-[#64748B]">Outflow Volume</p>
                <p className="mt-3 text-2xl font-display text-[#0B192C]">{sentVolume.toFixed(2)} TB</p>
                <p className="text-sm text-[#64748B] mt-2">Total sent across shown transactions.</p>
              </div>
              <div className="rounded-3xl border border-[#E2E8F0] bg-[#F8F9FA] p-5">
                <p className="text-xs uppercase tracking-[0.2em] text-[#64748B]">Inflow Volume</p>
                <p className="mt-3 text-2xl font-display text-[#0B192C]">{receivedVolume.toFixed(2)} TB</p>
                <p className="text-sm text-[#64748B] mt-2">Total received across shown transactions.</p>
              </div>
              <div className="rounded-3xl border border-[#E2E8F0] bg-[#F8F9FA] p-5">
                <p className="text-xs uppercase tracking-[0.2em] text-[#64748B]">Top Counterparty</p>
                <p className="mt-3 text-base font-semibold text-[#0B192C] break-words">{highestCounterparty}</p>
                <p className="text-sm text-[#64748B] mt-2">Most frequent partner in current history.</p>
              </div>
            </div>
          </div>

          <div className="md:col-span-3 lg:col-span-4 border border-[#D4AF37]/50 bg-[#F9F6ED] p-6 animate-fade-in" style={{ animationDelay: "0.14s" }}>
            <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#64748B] font-body">Action Center</p>
                <h3 className="mt-2 font-display text-2xl text-[#0B192C]">What business owners should do this week</h3>
                <div className="mt-5 grid gap-3 sm:grid-cols-2">
                  <div className="bg-white border border-[#E2E8F0] p-4">
                    <p className="text-sm font-semibold text-[#0B192C]">1. Review wallet health</p>
                    <p className="text-sm text-[#64748B] mt-1">Refresh balance and confirm enough TB for member campaign payouts.</p>
                  </div>
                  <div className="bg-white border border-[#E2E8F0] p-4">
                    <p className="text-sm font-semibold text-[#0B192C]">2. Plan ad credits</p>
                    <p className="text-sm text-[#64748B] mt-1">Submit requests early with campaign context to reduce approval delays.</p>
                  </div>
                  <div className="bg-white border border-[#E2E8F0] p-4">
                    <p className="text-sm font-semibold text-[#0B192C]">3. Watch transfer mix</p>
                    <p className="text-sm text-[#64748B] mt-1">Compare sent vs received to keep credit flow aligned with growth goals.</p>
                  </div>
                  <div className="bg-white border border-[#E2E8F0] p-4">
                    <p className="text-sm font-semibold text-[#0B192C]">4. Keep profile updated</p>
                    <p className="text-sm text-[#64748B] mt-1">Accurate business details improve support and request routing speed.</p>
                  </div>
                </div>
              </div>
              <div className="bg-white border border-[#E2E8F0] p-5">
                <p className="text-xs uppercase tracking-[0.2em] text-[#64748B]">Planning Signals</p>
                <div className="mt-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <CalendarCheck size={18} weight="duotone" className="text-[#D4AF37] mt-0.5" />
                    <p className="text-sm text-[#0B192C]">If net flow is negative, request extra ad credits before launch week.</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <GlobeHemisphereWest size={18} weight="duotone" className="text-[#0B192C] mt-0.5" />
                    <p className="text-sm text-[#0B192C]">Keep onboarding simple with a standard member setup checklist for faster activation.</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <WarningCircle size={18} weight="duotone" className="text-[#B45309] mt-0.5" />
                    <p className="text-sm text-[#0B192C]">High send volume with low receipts may indicate campaigns need pricing adjustments.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── Send TB Bucks ── */}
          <div data-testid="transfer-widget" className="md:col-span-2 bg-white border border-[#E2E8F0] shadow-sm animate-fade-in" style={{ animationDelay: "0.15s" }}>
            <div className="border-b border-[#E2E8F0] px-6 py-4 flex items-center gap-2">
              <PaperPlaneTilt size={18} weight="duotone" className="text-[#0B192C]" />
              <h3 className="font-display text-lg font-semibold text-[#0B192C]">Send TB Bucks</h3>
            </div>
            <div className="p-6">
              <form onSubmit={handleTransfer} className="space-y-5">
                <div>
                  <label className="text-xs font-semibold text-[#64748B] uppercase tracking-[0.2em] mb-2 block font-body">Recipient</label>
                  <input
                    type="text"
                    value={transferRecipient}
                    onChange={(e) => setTransferRecipient(e.target.value)}
                    placeholder="Email or username"
                    data-testid="transfer-recipient-input"
                    className="w-full px-4 py-2.5 border border-[#E2E8F0] bg-white text-sm font-body focus:outline-none focus:ring-2 focus:ring-[#0B192C] focus:border-transparent transition-all"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold text-[#64748B] uppercase tracking-[0.2em] mb-2 block font-body">Amount</label>
                    <input
                      type="number"
                      value={transferAmount}
                      onChange={(e) => setTransferAmount(e.target.value)}
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                      data-testid="transfer-amount-input"
                      className="w-full px-4 py-2.5 border border-[#E2E8F0] bg-white text-sm font-body focus:outline-none focus:ring-2 focus:ring-[#0B192C] focus:border-transparent transition-all"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-[#64748B] uppercase tracking-[0.2em] mb-2 block font-body">Description</label>
                    <input
                      type="text"
                      value={transferDescription}
                      onChange={(e) => setTransferDescription(e.target.value)}
                      placeholder="Optional note"
                      data-testid="transfer-description-input"
                      className="w-full px-4 py-2.5 border border-[#E2E8F0] bg-white text-sm font-body focus:outline-none focus:ring-2 focus:ring-[#0B192C] focus:border-transparent transition-all"
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={isLoading}
                  data-testid="transfer-submit-btn"
                  className="w-full py-3 bg-[#0B192C] text-white hover:bg-[#1A2E4A] font-body font-medium text-sm tracking-wide transition-all duration-200 disabled:opacity-40 flex items-center justify-center gap-2"
                >
                  <PaperPlaneTilt size={16} weight="bold" />
                  {isLoading ? "Processing..." : "Send TB Bucks"}
                </button>
              </form>
            </div>
          </div>

          {/* ── Request Credits ── */}
          <div data-testid="purchase-widget" className="md:col-span-2 bg-white border border-[#E2E8F0] shadow-sm animate-fade-in" style={{ animationDelay: "0.2s" }}>
            <div className="border-b border-[#E2E8F0] px-6 py-4 flex items-center gap-2">
              <Plus size={18} weight="bold" className="text-[#0B192C]" />
              <h3 className="font-display text-lg font-semibold text-[#0B192C]">Request Advertising Credits</h3>
            </div>
            <div className="p-6">
              <form onSubmit={handlePurchase} className="space-y-5">
                <div>
                  <label className="text-xs font-semibold text-[#64748B] uppercase tracking-[0.2em] mb-2 block font-body">Amount to Request</label>
                  <input
                    type="number"
                    value={purchaseAmount}
                    onChange={(e) => setPurchaseAmount(e.target.value)}
                    placeholder="Enter amount"
                    step="1"
                    min="1"
                    data-testid="purchase-amount-input"
                    className="w-full px-4 py-2.5 border border-[#E2E8F0] bg-white text-sm font-body focus:outline-none focus:ring-2 focus:ring-[#0B192C] focus:border-transparent transition-all"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#64748B] uppercase tracking-[0.2em] mb-2 block font-body">Request Details</label>
                  <textarea
                    value={purchaseNotes}
                    onChange={(e) => setPurchaseNotes(e.target.value)}
                    placeholder="Describe the campaign or business use case"
                    rows={4}
                    className="w-full px-4 py-3 border border-[#E2E8F0] bg-white text-sm font-body text-[#0B192C] placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#0B192C] focus:border-transparent transition-all"
                  />
                </div>
                <p className="text-sm text-[#64748B] font-body">
                  This generates an email to info@trilliumbucks.com with the requested amount, your account email, and campaign details.
                </p>
                <button
                  type="submit"
                  disabled={isLoading}
                  data-testid="purchase-submit-btn"
                  className="w-full py-3 border-2 border-[#D4AF37] text-[#B5952F] hover:bg-[#F9F6ED] font-body font-medium text-sm tracking-wide transition-all duration-200 disabled:opacity-40"
                >
                  {isLoading ? "Submitting..." : "Submit Request"}
                </button>
              </form>
            </div>
          </div>

          {/* ── Transaction History ── */}
          <div data-testid="transactions-widget" className="md:col-span-2 lg:col-span-4 bg-white border border-[#E2E8F0] shadow-sm animate-fade-in" style={{ animationDelay: "0.25s" }}>
            <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ClockCounterClockwise size={18} weight="duotone" className="text-[#0B192C]" />
                <h3 className="font-display text-lg font-semibold text-[#0B192C]">Recent Transactions</h3>
              </div>
              <button onClick={onFetchTransactions} className="text-xs text-[#D4AF37] hover:text-[#B5952F] font-body font-medium transition-colors">
                Refresh
              </button>
            </div>
            <div className="overflow-x-auto">
              {transactions.length > 0 ? (
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-[#0B192C]">
                      <th className="text-xs font-semibold uppercase tracking-[0.15em] text-[#64748B] py-3 px-6 font-body">Date</th>
                      <th className="text-xs font-semibold uppercase tracking-[0.15em] text-[#64748B] py-3 px-6 font-body">Description</th>
                      <th className="text-xs font-semibold uppercase tracking-[0.15em] text-[#64748B] py-3 px-6 font-body">With</th>
                      <th className="text-xs font-semibold uppercase tracking-[0.15em] text-[#64748B] py-3 px-6 font-body">Type</th>
                      <th className="text-xs font-semibold uppercase tracking-[0.15em] text-[#64748B] py-3 px-6 font-body text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((tx, i) => (
                      <tr key={tx.id || i} className="border-b border-[#E2E8F0] hover:bg-[#F8F9FA] transition-colors">
                        <td className="py-4 px-6 text-sm text-[#64748B] font-body">{formatDate(tx.date)}</td>
                        <td className="py-4 px-6 text-sm text-[#0B192C] font-body font-medium">{tx.description || "Transaction"}</td>
                        <td className="py-4 px-6 text-sm text-[#64748B] font-body">{tx.relatedUser || "—"}</td>
                        <td className="py-4 px-6">
                          <span className={`text-xs px-2 py-0.5 font-body font-medium ${
                            tx.amount && parseFloat(tx.amount) > 0 ? "bg-emerald-50 text-emerald-700" : "bg-[#F9F6ED] text-[#B5952F]"
                          }`}>
                            {tx.amount && parseFloat(tx.amount) > 0 ? "Received" : "Sent"}
                          </span>
                        </td>
                        <td className={`py-4 px-6 text-sm font-mono text-right font-medium ${
                          tx.amount && parseFloat(tx.amount) > 0 ? "text-emerald-600" : "text-[#0B192C]"
                        }`}>
                          {tx.amount && parseFloat(tx.amount) > 0 ? "+" : ""}{tx.amount || "0.00"} TB
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="p-16 text-center">
                  <ClockCounterClockwise size={32} weight="duotone" className="text-[#E2E8F0] mx-auto mb-3" />
                  <p className="text-[#64748B] font-body text-sm">No transactions yet</p>
                </div>
              )}
            </div>
          </div>

          {/* ── Profile ── */}
          <div data-testid="profile-widget" className="md:col-span-2 bg-white border border-[#E2E8F0] shadow-sm animate-fade-in" style={{ animationDelay: "0.3s" }}>
            <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center gap-2">
              <User size={18} weight="duotone" className="text-[#0B192C]" />
              <h3 className="font-display text-lg font-semibold text-[#0B192C]">Profile</h3>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <label className="text-xs font-semibold text-[#64748B] uppercase tracking-[0.2em] mb-2 block font-body">Full Name</label>
                <input type="text" value={profileName} onChange={(e) => setProfileName(e.target.value)} placeholder="Your name"
                  className="w-full px-4 py-2.5 border border-[#E2E8F0] bg-white text-sm font-body focus:outline-none focus:ring-2 focus:ring-[#0B192C] focus:border-transparent transition-all" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#64748B] uppercase tracking-[0.2em] mb-2 block font-body">Phone</label>
                <input type="tel" value={profilePhone} onChange={(e) => setProfilePhone(e.target.value)} placeholder="Your phone"
                  className="w-full px-4 py-2.5 border border-[#E2E8F0] bg-white text-sm font-body focus:outline-none focus:ring-2 focus:ring-[#0B192C] focus:border-transparent transition-all" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#64748B] uppercase tracking-[0.2em] mb-2 block font-body">Business</label>
                <div className="flex items-center gap-2">
                  <Buildings size={16} weight="duotone" className="text-[#64748B] shrink-0" />
                  <input type="text" value={profileBusiness} onChange={(e) => setProfileBusiness(e.target.value)} placeholder="Business name (optional)"
                    className="w-full px-4 py-2.5 border border-[#E2E8F0] bg-white text-sm font-body focus:outline-none focus:ring-2 focus:ring-[#0B192C] focus:border-transparent transition-all" />
                </div>
              </div>
              <button onClick={saveProfile}
                className="w-full py-3 bg-[#0B192C] text-white hover:bg-[#1A2E4A] font-body font-medium text-sm tracking-wide transition-all duration-200">
                Save Profile
              </button>
            </div>
          </div>

          {/* ── Business Toolkit ── */}
          <div className="md:col-span-2 bg-white border border-[#E2E8F0] shadow-sm animate-fade-in" style={{ animationDelay: "0.35s" }}>
            <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center gap-2">
              <CheckCircle size={18} weight="duotone" className="text-[#0B192C]" />
              <h3 className="font-display text-lg font-semibold text-[#0B192C]">Business Toolkit</h3>
            </div>
            <div className="p-6">
              <p className="text-sm text-[#64748B] leading-6">
                Built for live member operations. Use these practical tools to run campaigns, manage credits, and keep your customer experience consistent.
              </p>
              <div className="grid gap-4 mt-6 sm:grid-cols-2">
                <div className="rounded-3xl bg-[#F8F9FA] p-5 border border-[#E2E8F0]">
                  <p className="text-xs uppercase tracking-[0.2em] text-[#64748B]">Cashflow Planning</p>
                  <p className="mt-3 text-sm text-[#0B192C]">Track weekly in/outflow and set a minimum TB reserve before campaign launches.</p>
                </div>
                <div className="rounded-3xl bg-[#F8F9FA] p-5 border border-[#E2E8F0]">
                  <p className="text-xs uppercase tracking-[0.2em] text-[#64748B]">Campaign ROI Notes</p>
                  <p className="mt-3 text-sm text-[#0B192C]">Record which credit requests drove the best response so future approvals are data-backed.</p>
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* Footer */}
        <footer className="mt-16 pt-8 pb-4 border-t border-[#E2E8F0] text-center">
          <p className="font-display text-lg font-semibold text-[#0B192C] tracking-tight">Trillium Bucks</p>
          <p className="text-xs text-[#64748B] font-body mt-1">A Division of The Alliance Group</p>
          <p className="text-xs text-[#94A3B8] font-body mt-3">&copy; {new Date().getFullYear()} All rights reserved.</p>
        </footer>
      </main>

      <Toaster position="top-right" richColors />
    </div>
  );
};

// ─── App Controller ───────────────────────────────────────────
const Dashboard = () => {
  const [sessionToken, setSessionToken] = useState("");
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [username, setUsername] = useState("");
  const [balance, setBalance] = useState("0.00");
  const [availableBalance, setAvailableBalance] = useState("0.00");
  const [currencySymbol, setCurrencySymbol] = useState("TB");
  const [transactions, setTransactions] = useState([]);
  const sessionCheckRef = useRef(null);

  const handleSessionExpired = useCallback(() => {
    setIsConnected(false);
    setSessionToken("");
    localStorage.setItem("tb_session_expired", "true");
    localStorage.removeItem("tb_session_token");
    localStorage.removeItem("tb_connected");
  }, []);

  const applyBalance = useCallback((data) => {
    setBalance(data.balance || "0.00");
    setAvailableBalance(data.availableBalance || "0.00");
    setCurrencySymbol(data.currencySymbol || "TB");
  }, []);

  const validateSession = useCallback(async (token, email) => {
    try {
      const resp = await fetch(`${API}/cyclos/validate-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_token: token }),
      });
      const data = await resp.json();
      if (data.ok) {
        setSessionToken(token);
        setIsConnected(true);
        setUserEmail(email);
        setUsername(emailToUsername(email));
        applyBalance(data);
        localStorage.setItem("tb_connected", "true");
        localStorage.setItem("tb_session_token", token);
      } else {
        handleSessionExpired();
      }
    } catch {
      handleSessionExpired();
    }
  }, [handleSessionExpired, applyBalance]);

  // Restore session
  useEffect(() => {
    const savedToken = localStorage.getItem("tb_session_token");
    const savedEmail = localStorage.getItem("tb_user_email");
    if (savedToken && savedEmail) {
      validateSession(savedToken, savedEmail);
    }
  }, [validateSession]);

  // Periodic session check
  useEffect(() => {
    if (sessionToken && isConnected) {
      sessionCheckRef.current = setInterval(() => validateSession(sessionToken, userEmail), 5 * 60 * 1000);
    }
    return () => { if (sessionCheckRef.current) clearInterval(sessionCheckRef.current); };
  }, [sessionToken, isConnected, userEmail, validateSession]);

  // Login
  const handleLogin = async (email, password) => {
    setIsLoading(true);
    try {
      const resp = await fetch(`${API}/cyclos/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await resp.json();
      if (data.ok) {
        setSessionToken(data.sessionToken);
        setIsConnected(true);
        setUserEmail(email);
        setUsername(emailToUsername(data.username || email));
        applyBalance(data);
        localStorage.setItem("tb_user_email", email);
        localStorage.setItem("tb_session_token", data.sessionToken);
        localStorage.setItem("tb_connected", "true");
        localStorage.removeItem("tb_session_expired");
        toast.success("Signed in successfully");
        fetchTransactionsWithToken(data.sessionToken);
        return true;
      }

      toast.error(data.error || "Login failed. If you don't have an account, click Create one below.");
      return false;
    } catch {
      toast.error("Connection failed. Please try again.");
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateAccount = async ({ email, password, first_name, last_name }) => {
    setIsLoading(true);
    try {
      const resp = await fetch(`${API}/cyclos/create-account`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, first_name, last_name }),
      });
      const data = await resp.json();
      if (data.ok) {
        toast.success("Account created. Signing you in...");
        return await handleLogin(email, password);
      }

      toast.error(data.error || "Registration failed. Please try again.");
    } catch {
      toast.error("Registration failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
    return false;
  };

  // Logout
  const handleLogout = async () => {
    if (sessionToken) {
      try {
        await fetch(`${API}/cyclos/logout`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ session_token: sessionToken }),
        });
      } catch {}
    }

    setIsConnected(false);
    setSessionToken("");
    setTransactions([]);
    setBalance("0.00");
    setAvailableBalance("0.00");
    localStorage.removeItem("tb_session_token");
    localStorage.removeItem("tb_connected");
    localStorage.removeItem("tb_session_expired");
    toast.info("Logged out");
  };

  // Balance
  const fetchBalance = useCallback(async () => {
    if (!sessionToken) return;
    setIsLoading(true);
    try {
      const resp = await fetch(`${API}/cyclos/balance`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ session_token: sessionToken }) });
      const data = await resp.json();
      if (data.ok) { applyBalance(data); toast.success("Balance updated"); }
      else if (data.expired) { handleSessionExpired(); toast.error("Session expired"); }
    } catch { toast.error("Failed to fetch balance"); }
    finally { setIsLoading(false); }
  }, [sessionToken, handleSessionExpired, applyBalance]);

  // Transactions
  const fetchTransactionsWithToken = useCallback(async (token) => {
    try {
      const resp = await fetch(`${API}/cyclos/transactions`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ session_token: token }) });
      const data = await resp.json();
      if (data.ok && Array.isArray(data.transactions)) setTransactions(data.transactions.slice(0, 15));
      else if (data.expired) handleSessionExpired();
    } catch {}
  }, [handleSessionExpired]);

  const fetchTransactions = useCallback(() => {
    if (sessionToken) fetchTransactionsWithToken(sessionToken);
  }, [sessionToken, fetchTransactionsWithToken]);

  // Fetch transactions whenever session is restored (page refresh)
  useEffect(() => {
    if (sessionToken && isConnected) fetchTransactionsWithToken(sessionToken);
  }, [sessionToken, isConnected, fetchTransactionsWithToken]);

  // Transfer
  const handleTransfer = async (recipient, amount, description) => {
    setIsLoading(true);
    try {
      const resp = await fetch(`${API}/cyclos/transfer`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ session_token: sessionToken, recipient, amount, description: description || "TB Bucks Transfer" }) });
      const data = await resp.json();
      if (data.ok) { toast.success(`Sent ${data.amount} TB to ${data.recipient}`); fetchBalance(); fetchTransactions(); return true; }
      else if (data.expired) { handleSessionExpired(); toast.error("Session expired"); }
      else { toast.error(data.error || "Transfer failed"); }
    } catch { toast.error("Transfer failed"); }
    finally { setIsLoading(false); }
    return false;
  };

  // Purchase
  const handlePurchase = async (amount, notes = "") => {
    setIsLoading(true);
    try {
      const resp = await fetch(`${API}/cyclos/purchase`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ session_token: sessionToken, amount, user_email: userEmail, notes }) });
      const data = await resp.json();
      if (data.ok) { toast.success("Credit request submitted!"); return true; }
      else if (data.expired) { handleSessionExpired(); toast.error("Session expired"); }
      else { toast.error(data.error || "Request failed"); }
    } catch { toast.error("Request failed"); }
    finally { setIsLoading(false); }
    return false;
  };

  if (!isConnected) {
    return <LoginScreen onLogin={handleLogin} isLoading={isLoading} onCreateAccount={handleCreateAccount} />;
  }

  return (
    <DashboardView
      userEmail={userEmail}
      username={username}
      sessionToken={sessionToken}
      balance={balance}
      availableBalance={availableBalance}
      currencySymbol={currencySymbol}
      transactions={transactions}
      isLoading={isLoading}
      onLogout={handleLogout}
      onFetchBalance={fetchBalance}
      onFetchTransactions={fetchTransactions}
      onTransfer={handleTransfer}
      onPurchase={handlePurchase}
      onSessionExpired={handleSessionExpired}
    />
  );
};

function App() {
const VaultPage = () => (
  <div className="min-h-screen bg-[#F8F9FA]">
    <header className="bg-[#0B192C] py-5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          <div>
            <div className="w-10 h-10 bg-[#D4AF37] flex items-center justify-center rounded-full">
              <span className="text-[#0B192C] font-display font-bold text-lg">T</span>
            </div>
          </div>
          <div>
            <p className="text-sm text-white/70">Trillium Bucks Vault</p>
            <h1 className="text-xl font-semibold text-white">Brilliant Directories Vault Landing</h1>
          </div>
          <a href="/dashboard" className="text-sm text-[#D4AF37] hover:text-white">Open Dashboard</a>
        </div>
      </div>
    </header>

    <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="bg-white border border-[#E2E8F0] shadow-sm rounded-3xl overflow-hidden">
        <div className="p-8 md:p-12">
          <h2 className="font-display text-3xl font-semibold text-[#0B192C] mb-4">Vault integration for Brilliant Directories</h2>
          <p className="text-[#64748B] text-base leading-relaxed mb-8">
            Use this page as a dedicated Vault landing page, or embed the dashboard directly in Brilliant Directories using the snippet below.
          </p>

          <div className="grid gap-6 md:grid-cols-2 mb-10">
            <div className="rounded-3xl border border-[#E2E8F0] bg-[#F8F9FA] p-6">
              <h3 className="font-semibold text-[#0B192C] mb-3">Use as Vault landing</h3>
              <p className="text-sm text-[#64748B] mb-4">
                Direct users to <span className="font-mono text-[#0B192C]">https://vault.trilliumbucks.com/vault</span> from your Brilliant Directories menu.
              </p>
              <a href="/dashboard" className="inline-flex items-center justify-center rounded-full border border-[#D4AF37] px-5 py-3 text-sm font-medium text-[#0B192C] hover:bg-[#F9F6ED]">
                Open Vault Dashboard
              </a>
            </div>
            <div className="rounded-3xl border border-[#E2E8F0] bg-[#F8F9FA] p-6">
              <h3 className="font-semibold text-[#0B192C] mb-3">Embed in BD</h3>
              <p className="text-sm text-[#64748B] mb-4">
                Paste this snippet into a Brilliant Directories HTML block or landing page section.
              </p>
              <div className="rounded-2xl bg-white border border-[#E2E8F0] p-4">
                <pre className="whitespace-pre-wrap text-xs text-[#0B192C] font-mono">{`<iframe src="https://vault.trilliumbucks.com" style="width:100%;min-height:900px;border:none;" loading="lazy"></iframe>`}</pre>
              </div>
            </div>
          </div>

          <div className="rounded-3xl border border-[#E2E8F0] bg-[#F8F9FA] p-6">
            <h3 className="font-semibold text-[#0B192C] mb-3">Recommended BD setup</h3>
            <ul className="list-disc pl-5 space-y-2 text-[#64748B] text-sm">
              <li>Use an iframe block or full-width HTML section.</li>
              <li>Set the page title to “Vault” or “Trillium Bucks Dashboard”.</li>
              <li>Make sure BD page is accessible only to logged-in members.</li>
              <li>Keep the external app at <span className="font-mono text-[#0B192C]">https://vault.trilliumbucks.com</span>.</li>
            </ul>
          </div>
        </div>
      </div>
    </main>
  </div>
);

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/vault" element={<VaultPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
